import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import PopoutWindow from './PopoutWindow';

// A dockable / detachable shell for the workspace tool panel. The same children
// (the live tool controls) render in one of four placements, chosen by `dock`:
//   'left' / 'right' — docked in-flow beside the map (flex order)
//   'float'          — a draggable card floating over the app
//   'popout'         — a separate browser window (React portal, fully live)
// The map stays centred; only this panel relocates.

const PURPLE = '#3D1A6E';
const RAIL = '#EDE5F8';

function DockBtn({ active, title, onClick, children }) {
  return (
    <button title={title} onClick={onClick}
      style={{
        width: 26, height: 24, borderRadius: 5, cursor: 'pointer', fontFamily: 'inherit',
        border: `1px solid ${active ? PURPLE : RAIL}`, background: active ? PURPLE : 'white',
        color: active ? 'white' : PURPLE, fontSize: 12, lineHeight: 1, display: 'flex',
        alignItems: 'center', justifyContent: 'center', padding: 0,
      }}>
      {children}
    </button>
  );
}

function DockHeader({ dock, onDock, onDragStart }) {
  const floating = dock === 'float';
  return (
    <div
      onMouseDown={floating ? onDragStart : undefined}
      style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
        padding: '7px 10px', borderBottom: `1px solid ${RAIL}`, background: '#FAF8FD',
        cursor: floating ? 'move' : 'default', flexShrink: 0, userSelect: 'none',
      }}>
      <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: PURPLE }}>
        {floating ? '✥ Tools' : 'Tools'}
      </span>
      <div style={{ display: 'flex', gap: 4 }}>
        <DockBtn active={dock === 'left'} title="Dock left" onClick={() => onDock('left')}>◧</DockBtn>
        <DockBtn active={dock === 'right'} title="Dock right" onClick={() => onDock('right')}>◨</DockBtn>
        <DockBtn active={dock === 'float'} title="Float (drag anywhere)" onClick={() => onDock('float')}>❏</DockBtn>
        <DockBtn active={dock === 'popout'} title="Pop out to a new window" onClick={() => onDock('popout')}>⧉</DockBtn>
      </div>
    </div>
  );
}

export default function DockPanel({ dock, onDock, width = 280, floatPos, setFloatPos, children }) {
  const dragRef = useRef(null);

  // Drag handling for the floating card.
  useEffect(() => {
    if (dock !== 'float') return;
    function onMove(e) {
      const d = dragRef.current;
      if (!d) return;
      setFloatPos({ x: Math.max(0, e.clientX - d.dx), y: Math.max(0, e.clientY - d.dy) });
    }
    function onUp() { dragRef.current = null; document.body.style.userSelect = ''; }
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
  }, [dock, setFloatPos]);

  function startDrag(e) {
    dragRef.current = { dx: e.clientX - floatPos.x, dy: e.clientY - floatPos.y };
    document.body.style.userSelect = 'none';
  }

  const header = <DockHeader dock={dock} onDock={onDock} onDragStart={startDrag} />;
  const body = <div style={{ flex: 1, overflowY: 'auto', padding: '12px 14px' }}>{children}</div>;

  if (dock === 'popout') {
    return (
      <PopoutWindow title="Odinflow — Tools" width={width + 24} onClose={() => onDock('left')}>
        <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', background: 'white' }}>
          {header}{body}
        </div>
      </PopoutWindow>
    );
  }

  if (dock === 'float') {
    return (
      <div style={{
        position: 'fixed', top: floatPos.y, left: floatPos.x, width, maxHeight: '82vh', zIndex: 1400,
        background: 'white', border: `1.5px solid ${RAIL}`, borderRadius: 12,
        boxShadow: '0 18px 50px rgba(28,20,46,0.28)', display: 'flex', flexDirection: 'column', overflow: 'hidden',
      }}>
        {header}{body}
      </div>
    );
  }

  // Docked in-flow, left or right of the map (via flex order).
  return (
    <div style={{
      width, minWidth: width, height: '100%', background: 'white',
      borderRight: dock === 'left' ? `1.5px solid ${RAIL}` : 'none',
      borderLeft: dock === 'right' ? `1.5px solid ${RAIL}` : 'none',
      display: 'flex', flexDirection: 'column', zIndex: 100, order: dock === 'right' ? 2 : 0,
    }}>
      {header}{body}
    </div>
  );
}

// ── Per-tool docking ────────────────────────────────────────────────────────
// One workspace tool (Census, Network, Assign, …) placed independently:
// 'left' / 'right' (docked in the sidebar on the chosen side when it's the active
// tool), 'float' (a draggable card, portalled to <body> so it sits above the
// map), or 'popout' (its own window). Several tools can be floated/popped at
// once, across monitors. `render` returns the tool's controls lazily, so a hidden
// tool costs nothing. `rightEl` is the right dock zone's DOM node (portal target).
export function ToolSlot({ toolKey, label, active, dock, onDock, floatPos, setFloatPos, rightEl, idx = 0, render }) {
  const dragRef = useRef(null);
  useEffect(() => {
    if (dock !== 'float') return;
    function onMove(e) {
      const d = dragRef.current;
      if (!d) return;
      setFloatPos(toolKey, { x: Math.max(0, e.clientX - d.dx), y: Math.max(0, e.clientY - d.dy) });
    }
    function onUp() { dragRef.current = null; document.body.style.userSelect = ''; }
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
  }, [dock, toolKey, setFloatPos]);

  const docked = dock === 'left' || dock === 'right';
  const visible = dock === 'float' || dock === 'popout' || (docked && active);
  if (!visible) return null;
  const pos = floatPos || { x: 90 + idx * 34, y: 120 + idx * 34 };
  const content = render();

  function startDrag(e) {
    dragRef.current = { dx: e.clientX - pos.x, dy: e.clientY - pos.y };
    document.body.style.userSelect = 'none';
  }

  const controls = (
    <div style={{ display: 'flex', gap: 4 }}>
      <DockBtn active={dock === 'left'} title="Dock left" onClick={() => onDock(toolKey, 'left')}>◧</DockBtn>
      <DockBtn active={dock === 'right'} title="Dock right" onClick={() => onDock(toolKey, 'right')}>◨</DockBtn>
      <DockBtn active={dock === 'float'} title="Float (drag anywhere)" onClick={() => onDock(toolKey, 'float')}>❏</DockBtn>
      <DockBtn active={dock === 'popout'} title="Pop out to a new window" onClick={() => onDock(toolKey, 'popout')}>⧉</DockBtn>
    </div>
  );
  const headerBar = (draggable) => (
    <div onMouseDown={draggable ? startDrag : undefined}
      style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '6px 10px', borderBottom: `1px solid ${RAIL}`, background: '#FAF8FD', flexShrink: 0, userSelect: 'none', cursor: draggable ? 'move' : 'default' }}>
      <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: PURPLE }}>{draggable ? '✥ ' : ''}{label}</span>
      {controls}
    </div>
  );

  if (dock === 'popout') {
    return (
      <PopoutWindow title={`Odinflow — ${label}`} width={324} onClose={() => onDock(toolKey, 'left')}>
        <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', background: 'white' }}>
          {headerBar(false)}
          <div style={{ flex: 1, overflowY: 'auto', padding: '12px 14px' }}>{content}</div>
        </div>
      </PopoutWindow>
    );
  }

  if (dock === 'float') {
    // Portalled to <body> so it escapes the sidebar's stacking context and sits
    // above the map (position:fixed + high z-index).
    return createPortal(
      <div style={{ position: 'fixed', top: pos.y, left: pos.x, width: 300, maxHeight: '82vh', zIndex: 4000, background: 'white', border: `1.5px solid ${RAIL}`, borderRadius: 12, boxShadow: '0 18px 50px rgba(28,20,46,0.28)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {headerBar(true)}
        <div style={{ flex: 1, overflowY: 'auto', padding: '12px 14px' }}>{content}</div>
      </div>,
      document.body,
    );
  }

  // Docked: a compact control strip then the tool's controls. 'left' renders
  // where this ToolSlot sits (inside the left dock zone); 'right' portals into
  // the right dock zone so it appears on the far side of the map.
  const dockedNode = (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 4, marginBottom: 8 }}>{controls}</div>
      {content}
    </div>
  );
  if (dock === 'right') {
    return rightEl ? createPortal(dockedNode, rightEl) : null;
  }
  return dockedNode;
}
